import React from 'react';
import { openDB } from "idb";
import { v4 as uuid } from "uuid";
import { Button, Input, Label, Alert } from 'reactstrap';
import Draggable from 'react-draggable';
import './AudioCapturePopup.css';
import { convertCafToWav } from '../../utils/fileConverter';

const DB_NAME = "INSTRUCTJS_DB";
const STORE_NAMES = ["trainings", "media"];

async function initDB() {
  return openDB(DB_NAME, 2, {
    upgrade(db) {
      STORE_NAMES.forEach((storeName) => {
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, { keyPath: "id", autoIncrement: true });
        }
      });
    },
  });
}

class AudioCapturePopup extends React.Component {
  nodeRef = React.createRef();
  constructor(props) {
    super(props);
    // this.state = {
    //   showPopup: false,
    //   recording: false,
    //   mediaURL: null,
    //   devices: [],
    //   selectedAudioDevice: null,
    // };
    this.mediaRecorder = null;
    this.chunks = [];
    this.stream = null;
  }

  async componentDidMount() {
    await this.enumerateDevices();
    await this.loadMedia();
  }

  async componentDidUpdate(prevProps, prevState) {
    if (prevProps.currentStep?.id !== this.props.currentStep?.id) {
      await this.loadMedia();
    }
  }

  saveToDB = async (file, type) => {
    const db = await initDB();
    const mediaID = uuid();
    if(this.props.currentStep){
      const currentStep = {...this.props.currentStep, AudioFilepath: mediaID};
      this.props.updateState({currentStep});
    }

    const mediaRecord = { id: mediaID, file, type, date: new Date(), trainings: [this.props.currentTraining.Id]  };
    await db.add(STORE_NAMES[1], mediaRecord);
    this.setState({ 
      mediaURL: URL.createObjectURL(mediaRecord.file),
      normalizing: false,
      normalizationProgress: 0
     }, () => {
      console.log("AudioCapturePopup: Saved media to IndexedDB:", mediaRecord, " state after save:", this.state);
     });

    // refresh saved audio list
    // await this.loadMedia();
  };

  loadMedia = async () => {
    const db = await initDB();
    const all = await db.getAll(STORE_NAMES[1]);
    if(!all || all.length === 0) {
      this.setState({mediaURL: null});
      return;
    }
    console.log("AudioCapturePopup: Loaded all media from IndexedDB:", all);
    // Only keep audio entries
    const audioEntries = all.filter(m => m.type.startsWith("audio") && m.trainings?.includes(this.props.currentTraining?.Id));
    const curStepAudio = audioEntries.length > 0 ? audioEntries.filter(a => a.id === this.props.currentStep?.AudioFilepath) : null;
    this.setState({mediaURL: curStepAudio && curStepAudio.length > 0 ? URL.createObjectURL(curStepAudio[0].file) : null});
  };

  enumerateDevices = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audio = devices.filter(device => device.kind === "audioinput");
    this.setState({ 
      devices: audio, 
      selectedAudioDevice: audio.length > 0 ? audio[0].deviceId : null 
    });
  };

  startRecording = async () => {
    try {
      // Capture audio stream
      const { selectedAudioDevice } = this.state;

      // Build constraints
      const constraints = {
        audio: selectedAudioDevice
          ? { deviceId: { exact: selectedAudioDevice } }
          : true, // fallback to default device
        video: false
      };

      // Request media stream
      this.stream = await navigator.mediaDevices.getUserMedia(constraints);
      this.chunks = [];

      // Determine MIME type with fallback chain for older devices (e.g., iPad)
      let mimeType;
      if (MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")) {
        mimeType = "audio/ogg;codecs=opus";
      } else if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
        mimeType = "audio/webm;codecs=opus";
      } else if (MediaRecorder.isTypeSupported("audio/webm")) {
        mimeType = "audio/webm";
      } else if (MediaRecorder.isTypeSupported("audio/wav")) {
        mimeType = "audio/wav";
      } else if (MediaRecorder.isTypeSupported("audio/mpeg")) {
        mimeType = "audio/mpeg";
      } else {
        // Last resort: let the browser pick (may fail, but we tried)
        mimeType = "";
        console.warn("No supported MIME type found for MediaRecorder");
      }
      console.log("Selected MIME type:", mimeType);

      this.stream.getAudioTracks().forEach(track => {
        console.log("Track enabled:", track.enabled);
        if (!track.enabled) track.enabled = true;
      });

      this.mediaRecorder = new MediaRecorder(this.stream, { mimeType: mimeType });

      this.mediaRecorder.ondataavailable = e => {
        console.log("Data available from MediaRecorder:", e.data);
        if (e.data.size > 0) this.chunks.push(e.data);
      };

      this.mediaRecorder.onstop = async () => {
        const blob = new Blob(this.chunks, { type: mimeType });

        // --- Normalize automatically ---
        const arrayBuffer = await blob.arrayBuffer();
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);

        // Find peak sample
        let peak = 0;
        for (let ch = 0; ch < audioBuffer.numberOfChannels; ch++) {
          const channelData = audioBuffer.getChannelData(ch);
          for (let i = 0; i < channelData.length; i++) {
            peak = Math.max(peak, Math.abs(channelData[i]));
          }
        }

        // Gain factor (so loudest = 1.0)
        const gain = .95 / peak;

        // Apply gain
        for (let ch = 0; ch < audioBuffer.numberOfChannels; ch++) {
          const channelData = audioBuffer.getChannelData(ch);
          for (let i = 0; i < channelData.length; i++) {
            channelData[i] *= gain;
          }
        }

        // Render normalized buffer
        const offlineCtx = new OfflineAudioContext(
          audioBuffer.numberOfChannels,
          audioBuffer.length,
          audioBuffer.sampleRate
        );
        const bufferSource = offlineCtx.createBufferSource();
        bufferSource.buffer = audioBuffer;
        bufferSource.connect(offlineCtx.destination);
        bufferSource.start();

        // Simulated progress
        const renderDurationMs = (audioBuffer.duration * 1000); // estimate half time (tunable)
        let progress = 0;
        this.setState({ normalizationProgress: 0 }); // 👈 state for display (0–100)

        const progressTimer = setInterval(() => {
          progress += 5; // increment percentage
          if (progress > 95) progress = 95; // leave room for final jump
          this.setState({ normalizationProgress: progress });
        }, renderDurationMs / 20); // 20 updates total

        const renderedBuffer = await offlineCtx.startRendering();

        // Pipe normalized audio into MediaRecorder for WebM
        const liveCtx = new (window.AudioContext || window.webkitAudioContext)();
        const src = liveCtx.createBufferSource();
        src.buffer = renderedBuffer;

        const dest = liveCtx.createMediaStreamDestination();
        src.connect(dest);
        src.start();

        const normalizedChunks = [];
        const recorder = new MediaRecorder(dest.stream, { mimeType: mimeType });
        recorder.onerror = (err) => {
          console.error("Error during recording normalized audio:", err);
          this.setState({ recorderError: "Error during recording normalized audio: " + err.message });
        }
        recorder.ondataavailable = e => {
          if (e.data.size > 0)
            normalizedChunks.push(e.data);
        };
        recorder.onstop = async () => {
          clearInterval(progressTimer);
          const normalizedBlob = new Blob(normalizedChunks, { type: mimeType });
          await this.saveToDB(normalizedBlob, mimeType);
          audioCtx.close();
          liveCtx.close();
        };

        recorder.start();
        setTimeout(() => recorder.stop(), renderedBuffer.duration * 1000);
      };

      this.mediaRecorder.start();
      this.setState({ recording: true });

    } catch (err) {
      console.error("Error accessing microphone:", err);
      this.setState({ recorderError: "Error accessing microphone: " + err.message });
    }
  }

  stopRecording = () => {
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop();
    }
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
    }
    if(this.state.recording){
      this.setState({ 
        recording: false,
        normalizing: true
      });
    }
  };

  // handleFileUpload = async (e) => {
  //   const file = e.target.files[0];
  //   if (file) {
  //     await this.saveToDB(file, file.type || "audio");
  //   }
  // };

  handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const arrayBuffer = await file.arrayBuffer();
      const header = new TextDecoder().decode(new Uint8Array(arrayBuffer).slice(0, 4));
      let finalBlob = null;
      let mimeType = '';

      // ✅ Detect CAF format and convert using your existing method
      if (header === 'caff') {
        console.warn('CAF detected — converting to WAV...');
        finalBlob = await convertCafToWav(arrayBuffer); // use your existing method
        mimeType = 'audio/wav';
      } else {
        mimeType = file.type || 'audio/mpeg';
        finalBlob = new Blob([arrayBuffer], { type: mimeType });
      }

      // 💾 Save to IndexedDB or wherever your saveToDB method stores it
      await this.saveToDB(finalBlob, mimeType);
    } catch (err) {
      console.error('Error processing uploaded audio file:', err);
    }
  };

  openPopup = () => {
    this.setState({ showPopup: true });
  };

  closePopup = () => {
    this.stopRecording();
    // Release microphone tracks if any stream exists
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    this.setState({ showPopup: false });
  };

  renderControls = () => {
    const { recording, normalizing, mediaURL, devices, selectedAudioDevice } = this.state;
    return (
      <>
        <div>
          <Label>Audio Device:</Label>
          <Input
            type="select"
            value={selectedAudioDevice || ""}
            onChange={(e) => this.setState({ selectedAudioDevice: e.target.value })}
          >
            <option value="">Default</option>
            {devices.map((device) => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label || `Microphone ${device.deviceId}`}
              </option>
            ))}
          </Input>
        </div>

        <div >
          {!recording ? (normalizing ? (
            <Button style={{width: '100%'}} disabled>Normalizing Audio...</Button>
          ) : (
            <Button style={{width: '100%'}} onClick={this.startRecording}>Start Recording</Button>
          )) : ( 
            <Button style={{width: '100%'}} onClick={this.stopRecording}>Stop Recording</Button>
          )}
        </div>

        <Input style={{ paddingTop: '10px' }} type="file" accept="audio/*" onChange={this.handleFileUpload} />

        {mediaURL && (
          <div>
            <h4>Last Recorded</h4>
            <audio controls src={mediaURL}></audio>
          </div>
        )}
      </>
    );
  };

 renderPopup = () => {
    if (!this.state?.showPopup) return null;
    return (
      <div className="audio-popup-overlay">
        <Draggable handle=".audio-popup-header" nodeRef={this.nodeRef}>
          <div ref={this.nodeRef} className="audio-recorder-popup">
            <div className="audio-popup-header">
              <h3>Audio Recorder</h3>
            </div>

            {this.state.recorderError && (
              <Alert color="danger">{this.state.recorderError}</Alert>
            )}

            {this.state.normalizing && (
              <Alert color="info">
                Normalizing Audio: {this.state.normalizationProgress}%
              </Alert>
            )}

            {this.renderControls()}

            <Button
              className="audio-popup-close"
              disabled={this.state.recording || this.state.normalizing}
              onClick={this.closePopup}
            >
              Close
            </Button>
          </div>
        </Draggable>
      </div>
    );
  };

  render() {
    console.log("rendering AudioCapturePopup, with props: ", this.props, " state: ", this.state);
    return (
      <>
        <Button
          className={this.props.buttonClass}
          size="lg"
          onClick={this.openPopup}
        >
          {this.props.buttonText || "Open Audio Capture"}
        </Button>
        {this.renderPopup()}
      </>
    );
  }
}

export default AudioCapturePopup;
